import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, QRCode, Space, Spin, Typography, message } from 'antd';
import { startTotpEnrolment, confirmTotpEnrolment, totpUri } from '../services/mfaService';

const { Paragraph, Text } = Typography;

type Props = {
  email: string;
  onDone: () => void;
  onCancel?: () => void;
};

// Authenticator enrolment: fetch a secret, show it as a QR code, confirm one
// code. Used full-screen by RequireMfa and inline on the Profile page.
const MfaSetup: React.FC<Props> = ({ email, onDone, onCancel }) => {
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    startTotpEnrolment()
      .then(s => {
        if (!cancelled) setSecret(s);
      })
      .catch(err => {
        console.warn('AssociateSoftwareToken failed:', err);
        if (!cancelled) setError('Could not start authenticator setup. Please sign in again.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await confirmTotpEnrolment(code.trim());
      message.success('Authenticator app set up');
      onDone();
    } catch (err) {
      console.warn('VerifySoftwareToken failed:', err);
      setError('That code was not accepted. Check the time on your phone and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!secret) {
    return error ? <Alert type="error" message={error} showIcon /> : <Spin />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Paragraph style={{ marginBottom: 0 }}>
        Scan this QR code with an authenticator app such as Google Authenticator, Microsoft
        Authenticator or 1Password, then enter the 6-digit code it shows.
      </Paragraph>
      <QRCode type="svg" value={totpUri(secret, email)} size={180} />
      <Text type="secondary">
        Can&apos;t scan? Enter this key manually:{' '}
        <Text code copyable>
          {secret}
        </Text>
      </Text>
      <Input.OTP length={6} value={code} onChange={setCode} autoFocus />
      {error && <Alert type="error" message={error} showIcon />}
      <Space>
        <Button type="primary" onClick={handleConfirm} loading={busy} disabled={code.length !== 6}>
          Verify and enable
        </Button>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Space>
    </Space>
  );
};

export default MfaSetup;
